// SetupChecklist — persistent banner shown to reseller (role===customer)
// accounts that haven't finished configuring forwards yet. Sits above
// the channel feed; goes away automatically once both conditions are
// met:
//
//   1. fxRateRubPerUsd > 0 (the FX rate they apply to USD prices)
//   2. forwardTargets has at least one enabled entry (the Telegram
//      channel @Buyergrambot will push cards into)
//
// The reseller can't *dismiss* it permanently because dismissing without
// completing setup would just hide the next thing they need to do and
// leave them confused. Instead we keep it slim (single line on desktop,
// wraps gracefully on mobile) and the primary action button opens
// Settings → Forward directly via the parent's onOpenForwardSettings
// callback.
//
// Refreshes its state when the modal closes (parent passes a `bump`
// counter that ticks on close — see App.jsx wiring). That way as soon
// as the reseller saves their FX rate or a target, the banner re-fetches
// and either updates its label ("one step left") or disappears entirely.
import { useState, useEffect } from "react";
import { api } from "../api.js";
import { T_NEW } from "../theme.js";
import { useT } from "../i18n.jsx";

export function SetupChecklist({ user, onOpenForwardSettings, bump = 0 }) {
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [hasFx, setHasFx] = useState(false);
  const [hasTarget, setHasTarget] = useState(false);

  useEffect(() => {
    // Only resellers (customer role) need this checklist. Staff/admin
    // can't forward and won't have reseller_settings rows.
    if (!user || user.role !== "customer") {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const s = await api.getResellerSettings();
        if (cancelled) return;
        setHasFx(Number(s.fxRateRubPerUsd) > 0);
        setHasTarget(
          Array.isArray(s.forwardTargets) &&
          s.forwardTargets.some((t) => t && t.enabled && t.chatId),
        );
      } catch {
        // 401/network — silently no-op. The user will see the error in
        // the regular Settings modal if they open it.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, bump]);

  if (loading) return null;
  if (!user || user.role !== "customer") return null;
  if (hasFx && hasTarget) return null;

  // One step left vs both — the copy reads differently so it doesn't
  // feel like a generic "configure stuff" nag.
  const stepsLeft = (hasFx ? 0 : 1) + (hasTarget ? 0 : 1);
  const headline =
    stepsLeft === 2 ? t("setup.banner.both")
                    : !hasFx ? t("setup.banner.needFx")
                            : t("setup.banner.needTarget");

  return (
    <div
      role="region"
      aria-label={t("setup.banner.aria")}
      style={{
        background: T_NEW.paperSoft,
        borderBottom: `1px solid ${T_NEW.ink12}`,
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        fontFamily: T_NEW.fontSans,
      }}>
      {/* Small persimmon dot keeps it visually tied to the editorial
          stamp dots used elsewhere in the brand. */}
      <span style={{ display: "inline-block", width: 8, height: 8,
                     borderRadius: "50%", background: T_NEW.persimmon,
                     flexShrink: 0 }} />
      <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink,
                    lineHeight: 1.45, flex: 1, minWidth: 200 }}>
        <strong style={{ fontWeight: 700 }}>{headline}</strong>
        {" "}
        <span style={{ color: T_NEW.ink56 }}>{t("setup.banner.detail")}</span>
      </div>
      <button
        onClick={onOpenForwardSettings}
        style={{
          background: T_NEW.persimmon, color: T_NEW.paperOnInk,
          border: "none", borderRadius: T_NEW.r1,
          padding: "8px 14px", fontSize: T_NEW.micro, fontWeight: 700,
          cursor: "pointer", fontFamily: T_NEW.fontMono,
          textTransform: "uppercase", letterSpacing: T_NEW.monoTrack,
          flexShrink: 0,
        }}>
        {t("setup.banner.cta")}
      </button>
    </div>
  );
}
